import type { VariantSetDocument, VariantState } from '@/lib/concept-workbench/types';
import type { RenditionPatch, SetPatch } from '@/services/data/ConceptRenditionService';

/**
 * Turn "the store document changed" into the smallest set of row patches.
 *
 * WHY A DIFF AND NOT A BLIND WRITE. The autosave queue is keyed per row, so
 * writing every variant on every keystroke would make four rows contend for one
 * debounce window and would send a 9:16 row's geometry to the server because the
 * user nudged the 1:1. Worse, each write carries an optimistic-lock version, so
 * spurious writes manufacture conflicts on rows nobody touched.
 *
 * WHY IDENTITY COMPARISON IS SOUND HERE. The variant-set store is strictly
 * immutable — every mutation funnels through one `mutate()` that rebuilds only
 * the objects it changed and returns the SAME object when nothing changed. So
 * `next.variants[i] !== previous.variants[i]` is exact, not an approximation.
 * Value-comparing four variants' geometry maps on every drag frame would be the
 * approximation.
 *
 * Membership changes are deliberately NOT emitted. Adding or removing a ratio is
 * an INSERT/DELETE the persistence layer owns (`addRatio` / `removeRatio`); a
 * patch cannot create a row, so emitting one for an unknown id would be a write
 * that silently does nothing.
 */

export interface DocumentSyncPatches {
  set: SetPatch | null;
  variants: { id: string; patch: RenditionPatch }[];
}

export const NO_PATCHES: DocumentSyncPatches = { set: null, variants: [] };

function variantPatch(previous: VariantState, next: VariantState): RenditionPatch | null {
  const patch: RenditionPatch = {};
  if (previous.mediaId !== next.mediaId) patch.mediaId = next.mediaId;
  if (previous.followsSetImage !== next.followsSetImage) {
    patch.followsSetImage = next.followsSetImage;
  }
  if (previous.needsReview !== next.needsReview) patch.needsReview = next.needsReview;
  if (previous.isGenerating !== next.isGenerating) patch.isGenerating = next.isGenerating;
  if (previous.position !== next.position) patch.position = next.position;
  if (previous.framing !== next.framing) patch.framing = next.framing;
  if (previous.geometry !== next.geometry) patch.geometry = next.geometry;
  if (previous.overrides !== next.overrides) patch.overrides = next.overrides;
  return Object.keys(patch).length > 0 ? patch : null;
}

export function diffDocuments(
  previous: VariantSetDocument | null,
  next: VariantSetDocument | null,
): DocumentSyncPatches {
  if (!next || !previous || previous === next) return NO_PATCHES;
  if (previous.conceptId !== next.conceptId) return NO_PATCHES;

  const set: SetPatch = {};
  if (previous.layers !== next.layers) set.layers = next.layers;
  if (previous.mediaId !== next.mediaId) set.mediaId = next.mediaId;
  if (previous.fontScaleBase !== next.fontScaleBase) set.fontScaleBase = next.fontScaleBase;

  const before = new Map(previous.variants.map((variant) => [variant.id, variant]));
  const variants: DocumentSyncPatches['variants'] = [];
  for (const variant of next.variants) {
    const existing = before.get(variant.id);
    if (!existing || existing === variant) continue;
    const patch = variantPatch(existing, variant);
    if (patch) variants.push({ id: variant.id, patch });
  }

  return { set: Object.keys(set).length > 0 ? set : null, variants };
}

export interface SettledGeneration {
  id: string;
  mediaId: string | null;
}

/**
 * Persisted → store, for exactly one case: a variant the store still shows as
 * `isGenerating` whose persisted row has settled (`isGenerating: false`). The
 * server attaches generated media itself, so a tab that reloaded mid-request
 * never runs the promise chain that would have cleared the flag; the polled
 * rendition query is the only place it learns the outcome. `mediaId` is
 * returned only when it differs from what the store already holds. Nothing
 * else the store leads (geometry, layers) is ever pulled back.
 */
export function settledGenerations(
  persisted: VariantSetDocument | null,
  store: VariantSetDocument | null,
): SettledGeneration[] {
  if (!persisted || !store) return [];
  const settled: SettledGeneration[] = [];
  for (const stored of store.variants) {
    if (!stored.isGenerating) continue;
    const row = persisted.variants.find((variant) => variant.id === stored.id);
    if (!row || row.isGenerating) continue;
    settled.push({
      id: stored.id,
      mediaId: row.mediaId && row.mediaId !== stored.mediaId ? row.mediaId : null,
    });
  }
  return settled;
}
